import { useState } from "react"
import PropTypes from "prop-types"

import StripeJsContainer from "../stripe_js_container"
import CheckoutForm from "shared/components/stripe/checkout_form"

import {
  trackPaymentMethodUpdateStarted,
  trackPaymentMethodUpdateCompleted,
  trackPaymentMethodUpdateFailed,
  trackPaymentMethodUpdateMethodSelected,
} from "app/utils/mixpanel_tracking"

import { logCustomDatadogAction } from "app/scripts/shared_setup"

export default function UpdatePaymentInfo(props) {
  const [lastPaymentMethodType, setLastPaymentMethodType] = useState("card")
  const entrypoint = "billing_payment_method_update"

  const onCreateSubscriptionStart = paymentMethodType => {
    setLastPaymentMethodType(paymentMethodType)

    trackPaymentMethodUpdateStarted({
      paymentMethodType,
      requiresAction: false,
      entrypoint,
    })
  }

  const onCreateSubscriptionCancel = error => {
    trackPaymentMethodUpdateFailed({
      paymentMethodType: lastPaymentMethodType,
      requiresAction: false,
      entrypoint,
      errorType: error?.message || "payment_method_update_cancelled",
    })
  }

  const onPaymentMethodSelect = paymentMethodType => {
    setLastPaymentMethodType(paymentMethodType)
    trackPaymentMethodUpdateMethodSelected({
      paymentMethodType,
      requiresAction: false,
      isBillingUpdateFlow: true,
      entrypoint,
    })
  }

  const buttonConfig = {
    buttonType: "",
    cta: "Update payment",
    submittingCopy: "Updating...",
  }

  const processedAfterPaymentMethodSetup = async (paymentMethodId, requiresAction, checkoutFormReset) => {
    await fetch(props.updatePaymentUrl, {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": window.App.authenticityToken,
      },
      body: JSON.stringify({
        payment_method: paymentMethodId,
      }),
    })
      .then(result => {
        return result.json()
      })
      .then(async result => {
        if (result.payment_intent_client_secret) {
          logCustomDatadogAction("stripe_3ds_initiated", { entrypoint: "update_payment" })
          try {
            await requiresAction(result.payment_intent_client_secret, "")

            logCustomDatadogAction("stripe_3ds_confirmed", { entrypoint: "update_payment" })
            trackPaymentMethodUpdateCompleted({
              paymentMethodType: lastPaymentMethodType,
              requiresAction: true,
              entrypoint,
            })

            window.appendFlash("Payment info updated!", "success")
            window.location.assign(props.billingUrl)
          } catch (error) {
            logCustomDatadogAction("stripe_3ds_failed", {
              entrypoint: "update_payment",
              error_code: error?.code || error?.message || "unknown",
            })
            trackPaymentMethodUpdateFailed({
              paymentMethodType: lastPaymentMethodType,
              requiresAction: true,
              entrypoint,
              errorType: error?.message || "payment_method_3ds_failed",
            })

            window.appendFlash(error?.message || "Payment failed. Please try again.", "danger")
            checkoutFormReset()
          }
        } else if (result.redirect_to) {
          trackPaymentMethodUpdateCompleted({
            paymentMethodType: lastPaymentMethodType,
            requiresAction: !!result.requires_action,
            entrypoint,
          })

          window.appendFlash("Payment info updated!", "success")
          window.location.assign(result.redirect_to)
        } else {
          trackPaymentMethodUpdateFailed({
            paymentMethodType: lastPaymentMethodType,
            requiresAction: false,
            entrypoint,
            errorType: result.error || "payment_method_update_failed",
          })

          window.appendFlash("Payment info failed. Please contact us to get help.", "danger")
          checkoutFormReset()
          window.App.loadZendeskMessenger()
        }
      })
      .catch(response => {
        trackPaymentMethodUpdateFailed({
          paymentMethodType: lastPaymentMethodType,
          requiresAction: false,
          entrypoint,
          errorType: response?.message || "payment_method_update_network_error",
        })
        window.appendFlash("Payment info failed. Please contact us to get help.", "danger")
        checkoutFormReset()
        window.App.loadZendeskMessenger()
      })
  }

  return (
    <StripeJsContainer
      selectedPrice={props.currentPrice}
      selectedCurrency={props.currentCurrency}
      email={props.userEmail}
      isBillingUpdateFlow
    >
      <CheckoutForm
        buttonConfig={buttonConfig}
        email={props.userEmail}
        isBillingUpdateFlow={true}
        onCreateSubscriptionCancel={onCreateSubscriptionCancel}
        onCreateSubscriptionStart={onCreateSubscriptionStart}
        onPaymentMethodSelect={onPaymentMethodSelect}
        processedAfterPaymentMethodSetup={processedAfterPaymentMethodSetup}
        promoCodeConfig={null}
        subscriptionSuccessPath={props.billingUrl}
      />
    </StripeJsContainer>
  )
}

UpdatePaymentInfo.propTypes = {
  billingUrl: PropTypes.string.isRequired,
  currentCurrency: PropTypes.string.isRequired,
  currentPrice: PropTypes.number.isRequired,
  userEmail: PropTypes.string.isRequired,
  updatePaymentUrl: PropTypes.string.isRequired,
}
